import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useEa } from "./EaContext";
import { sectionOf } from "./eaFormat";

const txt = (el) => (el?.innerText || el?.textContent || "").replace(/\s+/g, " ").trim();
const visible = (el) => el.getClientRects().length > 0;

function toHex(c) {
  const m = (c || "").match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*([\d.]+))?/);
  if (!m || (m[4] !== undefined && Number(m[4]) === 0)) return null;
  return `#${[m[1], m[2], m[3]].map((x) => Number(x).toString(16).padStart(2, "0")).join("")}`.toUpperCase();
}

// SVG Recharts serializzato con dimensioni esplicite e stili essenziali inline.
function serializeSvg(svg) {
  const { width, height } = svg.getBoundingClientRect();
  const clone = svg.cloneNode(true);
  clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  clone.setAttribute("width", Math.round(width));
  clone.setAttribute("height", Math.round(height));
  clone.setAttribute("viewBox", `0 0 ${Math.round(width)} ${Math.round(height)}`);
  const src = svg.querySelectorAll("text, tspan");
  const boost = Math.min(Math.max(width / 820, 1), 1.9);  // testi leggibili dopo il ridimensionamento in A4
  clone.querySelectorAll("text, tspan").forEach((t, i) => {
    const cs = getComputedStyle(src[i]);
    t.setAttribute("font-size", `${Math.round(Math.max(parseFloat(cs.fontSize) || 11, 10) * boost)}px`);
    if (!t.getAttribute("fill")) t.setAttribute("fill", cs.fill && cs.fill !== "none" ? cs.fill : "#64748B");
    t.setAttribute("font-family", "Helvetica");
  });
  return new XMLSerializer().serializeToString(clone);
}

function chartLegend(card) {
  return [...card.querySelectorAll(".recharts-legend-item-text, [data-testid$='-legend'] span")].map(txt).filter(Boolean).join(" · ");
}

function tableBlock(t, title) {
  const tds = [...t.querySelectorAll("tbody td")];
  const isHeat = tds.length > 20 && tds.filter((td) => !txt(td) && toHex(getComputedStyle(td).backgroundColor)).length > tds.length * 0.5;
  const columns = [...t.querySelectorAll("thead th")].map(txt);
  const allTrs = [...t.querySelectorAll("tbody tr")].filter(visible);
  const trs = allTrs.slice(0, 400);
  if (isHeat) {
    return { type: "heatmap", title, subtitle: `Scala colori (chiaro → scuro): ${txt(t.nextElementSibling)}`, columns: columns.slice(1), categories: trs.map((tr) => txt(tr.cells[0])),
      cell_colors: trs.map((tr) => [...tr.cells].slice(1).map((td) => toHex(getComputedStyle(td).backgroundColor))) };
  }
  const all = trs.map((tr) => ({ cells: [...tr.cells].map(txt), total: tr.hasAttribute("data-pdf-total") })).filter((r) => r.cells.some(Boolean));
  if (!columns.length && all.length) all.shift();
  const cols = columns.length ? columns : [...(trs[0]?.cells || [])].map(txt);
  const MAX = 60;
  const isTot = (r) => r.total || /^totale\b/i.test(r.cells[0] || "");
  const kept = all.length > MAX ? [...all.filter((r) => !isTot(r)).slice(0, MAX), ...all.filter(isTot)] : all;
  return { type: "table", title, columns: cols, rows: kept.map((r) => r.cells), total_rows: kept.map((r, i) => (isTot(r) ? i : -1)).filter((i) => i >= 0).slice(0, 20),
    subtitle: all.length > MAX ? `Prime ${MAX} righe di ${allTrs.length}: l'elenco completo è esportabile in Excel dalla pagina.` : null };
}

const pct = (s) => { const m = (s || "").match(/([+\-−]?\d+(?:[.,]\d+)?)\s*%/); return m ? Number(m[1].replace("−", "-").replace(",", ".")) : null; };

// Interpretazione a regole sui dati della pagina (scostamenti, budget, contratto).
function autoInsights(blocks) {
  const out = [];
  const MONTH = /^(gen|feb|mar|apr|mag|giu|lug|ago|set|ott|nov|dic)/i;
  const body = (b) => b.rows.filter((_, i) => !(b.total_rows || []).includes(i));
  blocks.filter((b) => b.type === "table" && body(b).length && body(b).every((r) => MONTH.test(r[0] || ""))).forEach((b) => {
    const ci = b.columns.findIndex((c) => /scostamento|^δ|delta/i.test(c));
    if (ci < 1) return;
    const vals = body(b).map((r) => ({ k: r[0], v: pct(r[ci]) })).filter((x) => x.v != null);
    if (vals.length < 2) return;
    const hi = vals.reduce((a, x) => (x.v > a.v ? x : a)), lo = vals.reduce((a, x) => (x.v < a.v ? x : a));
    const up = vals.filter((x) => x.v > 0).length;
    out.push({ level: up > vals.length / 2 ? "warning" : "ok", title: `${b.title || b.columns[ci]}: ${up} ${up === 1 ? "mese" : "mesi"} in aumento su ${vals.length}`,
      text: `Aumento maggiore: ${hi.k} (${hi.v > 0 ? "+" : ""}${hi.v.toLocaleString("it-IT")}%). Riduzione maggiore: ${lo.k} (${lo.v > 0 ? "+" : ""}${lo.v.toLocaleString("it-IT")}%).` });
  });
  blocks.filter((b) => b.type === "kpis").flatMap((b) => b.items).forEach((k) => {
    const p = pct(k.sub);
    if (/vs budget/i.test(k.sub || "") && p != null) {
      out.push({ level: p > 5 ? "alert" : p > 0 ? "warning" : "ok", title: `${k.label}: ${p > 0 ? "sopra" : "entro"} il budget`,
        text: `${k.value} · ${k.sub}. ${p > 5 ? "Scostamento significativo: verificare prezzi, volumi e coperture." : p > 0 ? "Scostamento contenuto, da monitorare nei prossimi mesi." : "Andamento in linea o migliore del budget."}` });
    }
    if (/rispetto al contratto/i.test(k.sub || "") && p != null && Math.abs(p) > 10) {
      out.push({ level: "warning", title: `Consumo ${p > 0 ? "superiore" : "inferiore"} al contrattuale`, text: `${k.sub}. Uno scostamento oltre il 10% può comportare penali o richiedere un aggiornamento del contratto.` });
    }
  });
  return out.slice(0, 12);
}

// Costruisce il report dalla pagina visibile: KPI, grafici, tabelle e note nell'ordine in cui compaiono.
export function buildPageSpec(root, extraFilters) {
  const blocks = [];
  const push = (b) => {
    const last = blocks[blocks.length - 1];
    if (b.type === "kpis" && !b.title && last?.type === "kpis") return last.items.push(...b.items);
    if (b.type === "notes" && last?.type === "notes") return last.notes.push(...b.notes);
    blocks.push(b);
  };
  const handled = new Set();
  root.querySelectorAll("[data-pdf], table").forEach((el) => {
    if (!visible(el) || [...handled].some((h) => h !== el && h.contains(el) && h.tagName === "TABLE")) return;
    const kind = el.tagName === "TABLE" ? "table" : el.dataset.pdf;
    if (kind === "section") {
      push({ type: "kpis", title: txt(el.querySelector("h2")), items: [] });
    } else if (kind === "kpi") {
      if (el.parentElement.closest("[data-pdf='kpi']")) return;
      const nested = [...el.querySelectorAll("[data-pdf='kpi']")].map((n) => `${txt(n.querySelector("[data-pdf-label]"))}: ${txt(n.querySelector("[data-pdf-value]"))}`);
      const own = [...el.querySelectorAll("[data-pdf-sub]")].filter((s) => s.closest("[data-pdf='kpi']") === el).map(txt);
      const band = el.querySelector("[data-pdf-label]");
      const bs = band ? getComputedStyle(band) : null;
      const stops = bs ? (bs.backgroundImage.match(/rgba?\([^)]+\)/g) || []).map((c) => c.match(/[\d.]+/g).slice(0, 3).map(Number)) : [];
      const mix = stops.length ? `rgb(${[0, 1, 2].map((i) => Math.round(stops.reduce((a, c) => a + c[i], 0) / stops.length)).join(", ")})` : null;
      const color = bs ? toHex(mix) || toHex(bs.backgroundColor) : null;
      push({ type: "kpis", items: [{ color, label: txt(el.querySelector("[data-pdf-label]")), value: txt(el.querySelector("[data-pdf-value]")) || "—", sub: [...own, ...nested].filter(Boolean).join(" · ").slice(0, 600) || null }] });
    } else if (kind === "chart") {
      const title = txt(el.querySelector("[data-pdf-title]")), subtitle = txt(el.querySelector("[data-pdf-subtitle]"));
      const svgs = [...el.querySelectorAll("svg.recharts-surface")].filter((s) => s.getBoundingClientRect().width > 150 && !s.closest("[data-pdf='chart'] [data-pdf='chart']"));
      const legend = chartLegend(el);
      svgs.forEach((s, i) => push({ type: "svg", title: i ? null : title, subtitle: i ? legend || null : [subtitle, legend && `Legenda: ${legend}`].filter(Boolean).join(" — ") || null, svg: serializeSvg(s) }));
      el.querySelectorAll("table").forEach((t) => { if (visible(t)) { push(tableBlock(t, svgs.length ? null : title)); handled.add(t); } });
      if (!svgs.length && !el.querySelector("table") && title) push({ type: "notes", notes: [{ title, text: txt(el).replace(title, "").trim().slice(0, 1500) || "—" }] });
    } else if (kind === "table") {
      if (handled.has(el) || el.closest("[data-pdf='chart']")) return;
      handled.add(el);
      push(tableBlock(el, txt(el.closest(".card-elevated")?.querySelector("[data-pdf-title]")) || null));
    } else if (kind === "note") {
      if (el.closest("[data-pdf='chart']") && el.closest("[data-pdf='chart']").querySelector("svg.recharts-surface") === null && !el.dataset.pdfLevel) return;
      const level = el.dataset.pdfLevel || "info";
      const ctx = el.closest("[data-pdf-ctx]")?.dataset.pdfCtx;
      const t0 = txt(el.querySelector("[data-pdf-ntitle]")), x = txt(el.querySelector("[data-pdf-ntext]")) || txt(el);
      const t = ctx && t0 ? `${ctx} – ${t0}` : t0;
      if (x) push({ type: "notes", title: "Indicazioni e note", notes: [{ level, title: t || null, text: x.slice(0, 1500) }] });
    }
  });
  const rules = autoInsights(blocks);
  if (rules.length) {
    const at = blocks.findIndex((b) => b.type !== "kpis");
    blocks.splice(at < 0 ? blocks.length : at, 0, { type: "notes", title: "Interpretazione dei risultati", notes: rules });
  }
  const h1 = root.querySelector("[data-testid='ea-page-title']");
  const periods = [...root.querySelectorAll("[data-pdf-filter]")].filter(visible).map(txt);
  const selects = periods.length ? [] : [...root.querySelectorAll("select")].filter((s) => visible(s) && s.selectedOptions[0]).map((s) => txt(s.selectedOptions[0]));
  const filters = [...extraFilters, ...periods, ...selects.filter((x) => !extraFilters.some((f) => f.endsWith(x)))];
  const sub = txt(h1?.parentElement?.querySelector("p")).split(/(?<=\.)\s+/).filter((x) => !/\bclic\b/i.test(x)).join(" ");
  return { title: txt(h1) || "Energy Analytics", subtitle: sub || null, filters: filters.slice(0, 12), blocks: blocks.slice(0, 60) };
}

export function EaPdfButton() {
  const { pathname } = useLocation();
  const { filters, nav } = useEa();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const root = document.getElementById("ea-page-root");
      const picker = txt(root.querySelector("[data-testid='ea-point-picker']"));
      const spec = buildPageSpec(root, [`Anno ${filters.year || nav?.year || ""}`, picker && `Punti: ${picker}`].filter(Boolean));
      const section = sectionOf(pathname);
      const filename = `Adybo_EA_${spec.title.replace(/[^A-Za-z0-9]+/g, "_")}_${filters.year || nav?.year || ""}`;
      const r = await api.post("/ea/report.pdf", { ...spec, section, filename, company_id: filters.company || undefined }, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: `${filename}.pdf` });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Generazione PDF non riuscita");
    } finally {
      setBusy(false);
    }
  };
  return (
    <button onClick={run} disabled={busy} className="h-9 inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 hover:border-slate-400 disabled:opacity-60" data-testid="ea-pdf-download" title="Scarica la sezione in PDF con logo e riferimenti Adybo">
      {busy ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} PDF
    </button>
  );
}
